import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Plus } from "lucide-react";
import { toast } from "sonner";
import { inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Reveal } from "@/components/Reveal";

const FEATURED_IMAGE = "https://images.unsplash.com/photo-1649083048198-f57bd8fb7a3b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Featured({ products }) {
  const { add, setOpen } = useCart();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section data-testid="featured-section" className="bg-white">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 py-16 sm:px-6 sm:py-24 lg:grid-cols-12 lg:gap-16 lg:px-8">
        <div ref={ref} className="relative overflow-hidden rounded-2xl img-frame lg:col-span-7">
          <motion.img
            src={FEATURED_IMAGE}
            alt="The Minimalist Living Collection styled in a bright modern home"
            style={{ y: imgY }}
            className="h-full min-h-[320px] w-full scale-110 object-cover lg:min-h-[560px]"
            loading="lazy"
          />
          <div className="absolute left-5 top-5 rounded-full bg-white/90 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-900 backdrop-blur">
            Featured Collection
          </div>
        </div>

        <div className="flex flex-col justify-center lg:col-span-5">
          <Reveal>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-neutral-400">
              <span className="mr-2 font-serif italic text-neutral-500">04</span>Featured
            </p>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl lg:text-4xl">
              The Minimalist <span className="font-serif font-medium italic tracking-normal">Living</span> Collection
            </h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-neutral-500 sm:text-base">
              Elevate your living space with functional pieces designed to reduce visual clutter and boost everyday efficiency.
            </p>
          </Reveal>

          <div className="mt-8 space-y-3">
            {products.slice(0, 3).map((p, i) => (
              <Reveal key={p.slug} delay={0.08 * i}>
                <div className="group flex items-center gap-4 rounded-xl border border-neutral-100 bg-background p-3 transition-colors duration-300 hover:border-neutral-300" data-testid={`featured-item-${p.slug}`}>
                  <Link to={`/product/${p.slug}`} className="shrink-0">
                    <img src={p.image} alt={p.name} className="h-16 w-16 rounded-lg object-cover img-frame" loading="lazy" />
                  </Link>
                  <div className="min-w-0 flex-1">
                    <Link to={`/product/${p.slug}`}>
                      <p className="truncate text-sm font-semibold text-neutral-900 transition-colors group-hover:text-neutral-600">{p.name}</p>
                    </Link>
                    <p className="mt-0.5 text-sm font-bold">{inr(p.price)}
                      {p.compare_at_price && <span className="ml-2 text-xs font-normal text-neutral-400 line-through">{inr(p.compare_at_price)}</span>}
                    </p>
                  </div>
                  <button
                    data-testid={`featured-add-${p.slug}`}
                    onClick={() => {
                      add(p, 1);
                      toast.success(`${p.name} added to your bag`, { action: { label: "View Bag", onClick: () => setOpen(true) } });
                    }}
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-neutral-200 transition-all duration-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white"
                    aria-label={`Add ${p.name} to bag`}
                  >
                    <Plus size={15} />
                  </button>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <Link
              to="/shop?featured=true"
              data-testid="shop-collection-cta"
              className="group mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-neutral-900 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-neutral-700"
            >
              Shop Collection
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
